/**
 * OAuth Configuration — GenesisBox (frontend)
 *
 * These are the PUBLIC client IDs and redirect URIs.
 * Client secrets live only on the server.
 */
import { config } from './environment';

export const OAUTH_CONFIG = {
  google: {
    clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    scope: 'openid email profile',
    redirectPath: '/auth/google/callback',
  },
  microsoft: {
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    scope: 'openid email profile User.Read',
    redirectPath: '/auth/microsoft/callback',
  },
} as const;

/**
 * Build the full OAuth authorization URL.
 * Opens in a popup or redirect — the server handles the callback.
 */
export function buildOAuthUrl(provider: 'google' | 'microsoft'): string {
  const cfg = OAUTH_CONFIG[provider];
  const apiBase = config.apiBaseUrl;
  const redirectUri = `${apiBase}${cfg.redirectPath}`;

  const params = new URLSearchParams({
    client_id: cfg.clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: cfg.scope,
    access_type: 'offline',
    prompt: 'select_account',
    state: generateState(),
  });

  return `${cfg.authUrl}?${params.toString()}`;
}

export function isDevLoginEnabled(): boolean {
  const isLocalHost =
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname);

  // Dev login is a localhost-only convenience and ships hardcoded credentials.
  // Never expose it on a deployed host, even if VITE_ENABLE_DEV_LOGIN=true
  // leaked into a production build via .env.local.
  if (typeof window !== 'undefined' && !isLocalHost) return false;

  const envFlag = import.meta.env.VITE_ENABLE_DEV_LOGIN;
  if (envFlag === 'false') return false;
  if (envFlag === 'true') return true;

  return config.isDevelopment || isLocalHost;
}

export function getDevLoginCredentials(): { username: string; password: string } {
  return {
    username: import.meta.env.VITE_DEV_LOGIN_USERNAME || 'AdminTest',
    password: import.meta.env.VITE_DEV_LOGIN_PASSWORD || '300101',
  };
}

/** CSRF state token */
function generateState(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  const state = Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  sessionStorage.setItem('oauth_state', state);
  return state;
}

export function verifyState(state: string): boolean {
  const stored = sessionStorage.getItem('oauth_state');
  sessionStorage.removeItem('oauth_state');
  return stored === state;
}
